import {
  emptyFilter,
  isGroup,
  type Filter,
  type FilterCondition,
  type FilterNode,
  type FilterOperator,
  type Json,
} from "@estelle/domain";

/**
 * The URL form of a filter.
 *
 * Deliberately not the storage form. `kind` is structural — a node with
 * children is a group — so it is dropped and rebuilt, and the keys are single
 * letters. A three-condition filter goes from about 320 characters to about
 * 150, which is the difference between a link that survives being pasted into
 * a chat window and one that gets wrapped and broken.
 *
 * Still JSON, and still readable in the address bar. Base64 would be shorter
 * again and would make a malformed link impossible to diagnose by looking at
 * it.
 */
interface WireCondition {
  f: string;
  o: string;
  v?: Json;
}

interface WireGroup {
  c: "and" | "or";
  n: WireNode[];
}

type WireNode = WireCondition | WireGroup;

function toWire(node: FilterNode): WireNode {
  if (isGroup(node)) {
    return { c: node.combinator, n: node.children.map(toWire) };
  }
  return node.value === undefined
    ? { f: node.field, o: node.operator }
    : { f: node.field, o: node.operator, v: node.value };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function fromWire(raw: unknown): FilterNode | null {
  if (!isRecord(raw)) return null;

  if (Array.isArray(raw.n)) {
    const children = raw.n
      .map(fromWire)
      .filter((child): child is FilterNode => child !== null);
    return {
      kind: "group",
      combinator: raw.c === "or" ? "or" : "and",
      children,
    };
  }

  if (typeof raw.f !== "string" || typeof raw.o !== "string") return null;

  const condition: FilterCondition = {
    kind: "condition",
    field: raw.f,
    // Not narrowed to the operator union here. An unknown operator is a stale
    // link, and validateFilter reports it against the module's fields with a
    // message the builder can show — better than silently dropping the clause.
    operator: raw.o as FilterOperator,
  };
  if (raw.v !== undefined) condition.value = raw.v as Json;
  return condition;
}

/**
 * Encode a filter for the address bar.
 *
 * Returns undefined for an empty filter so the parameter disappears rather
 * than sitting there as `?filter={"c":"and","n":[]}` on every page.
 */
export function encodeFilter(filter: Filter): string | undefined {
  if (filter.children.length === 0) return undefined;
  return JSON.stringify(toWire(filter));
}

/**
 * Decode whatever is in the URL.
 *
 * Tolerant by design. A truncated or hand-edited link produces an empty
 * filter and a list that works, not a crash — and anything structurally
 * intact but semantically stale (a renamed field, a removed operator) is
 * carried through so `validateFilter` can say what is wrong with it.
 */
export function decodeFilter(raw: unknown): Filter {
  if (typeof raw !== "string" || raw === "") return emptyFilter();

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return emptyFilter();
  }

  const node = fromWire(parsed);
  return node && isGroup(node) ? node : emptyFilter();
}
